import { TOKEN_RECOVERY_EPISODE_TIMEOUT_MS } from '../tokenRecovery';
import {
  createTokenRecoveryDiagnostic,
  sanitizeTokenRecoveryDiagnostic,
  type TokenRecoveryDiagnostic,
  type TokenRecoveryTrigger
} from '../tokenRecoveryDiagnostics';
import {
  createDestroyedRefreshResult,
  createDifferentAuthUserRefreshResult
} from '../refreshResult';
import {
  CoordinationRecoveryReporter,
  createCoordinationFailure
} from './CoordinationRecoveryReporter';
import { RefreshExecution, createTokenRefreshFailureResult } from './RefreshExecution';
import { RefreshDelegation } from './RefreshDelegation';
import {
  RefreshRequests,
  getTokenResponseUserId,
  getTokenRefreshRequestStartedAt,
  type PendingRefreshRequest
} from './RefreshRequests';
import type {
  TokenRefreshTransport,
  TokenRefreshCallback,
  TokenUpdateCallback,
  TokenRefreshResultCallback,
  TokenRefreshRequest,
  TokenRefreshSuccess,
  TokenRefreshFailure,
  TokenRefreshAttemptResult,
  BroadcastMessage
} from './types';

export type {
  TokenRefreshCallback,
  TokenUpdateCallback,
  TokenRefreshResultCallback
} from './types';

const createRefreshOwnedByAnotherTabResult = (): TokenRefreshAttemptResult => ({
  success: false,
  error: 'Coordinated token refresh is already owned by another tab',
  terminalFailure: false
});

const createNoUsableTokenRefreshResult = (): TokenRefreshAttemptResult => ({
  success: false,
  error: 'Token refresh did not produce a usable token',
  terminalFailure: false
});

export class TokenRefreshCoordinator {
  private onTokenRefreshResultCallback: TokenRefreshResultCallback | null = null;

  private authUserId: string | null = null;

  private readonly execution: RefreshExecution;

  private readonly requests: RefreshRequests;

  private readonly delegation = new RefreshDelegation();

  private readonly transport: TokenRefreshTransport;

  private readonly recoveryReporter: CoordinationRecoveryReporter;

  private isDestroyed = false;

  constructor(transport: TokenRefreshTransport) {
    this.transport = transport;
    this.recoveryReporter = new CoordinationRecoveryReporter(transport.tabId);
    this.execution = new RefreshExecution({
      tabId: transport.tabId,
      isLeader: () => this.transport.isLeader(),
      isCurrentAuthUser: userId => this.isCurrentAuthUser(userId)
    });
    this.requests = new RefreshRequests({
      tabId: transport.tabId,
      getLeaderId: () => this.transport.getLeaderId(),
      canApplyToken: (requestedUserId, responseUserId) =>
        this.isCurrentAuthUser(requestedUserId) && requestedUserId === responseUserId
    });
  }

  public handleMessage({ type, data }: BroadcastMessage): boolean {
    if (this.isDestroyed) {
      return false;
    }

    switch (type) {
      case 'TOKEN_REFRESH_SUCCESS':
        this.handleTokenRefreshSuccess(data as TokenRefreshSuccess);
        return true;
      case 'TOKEN_REFRESH_FAILED':
        this.handleTokenRefreshFailed(data as TokenRefreshFailure);
        return true;
      case 'TOKEN_REFRESH_REQUEST':
        this.handleTokenRefreshRequest(data as TokenRefreshRequest);
        return true;
      case 'TOKEN_REFRESH_STARTED':
        this.requests.handleStarted(data);
        return true;
      default:
        return false;
    }
  }

  public async requestTokenRefresh(
    requestedByUserId?: string | null,
    trigger: TokenRecoveryTrigger = 'token_demand'
  ): Promise<TokenRefreshAttemptResult> {
    const userId = requestedByUserId ?? this.authUserId;
    const startedAt = performance.now();
    const recovery = createTokenRecoveryDiagnostic(
      trigger,
      this.transport.tabId,
      this.transport.getLeaderId(),
      this.transport.isLeader() ? 'leader' : 'follower'
    );
    const result = await this.performTokenRefresh(userId, recovery);

    if (this.isDestroyed || this.authUserId !== userId) {
      return result;
    }

    const tokenUsable = this.onTokenRefreshResultCallback?.(result);
    this.recoveryReporter.reportFailure(result, recovery, startedAt, tokenUsable);

    return result;
  }

  public broadcastTokenUpdate(tokenData: TokenRefreshSuccess): void {
    if (!this.isDestroyed) {
      this.transport.postMessage({
        type: 'TOKEN_REFRESH_SUCCESS',
        data: tokenData
      });
    }
  }

  public setTokenRefreshCallback(callback: TokenRefreshCallback): void {
    this.execution.setCallback(callback);
  }

  public setTokenUpdateCallback(callback: TokenUpdateCallback): void {
    this.requests.setTokenUpdateCallback(callback);
  }

  public setTokenRefreshResultCallback(callback: TokenRefreshResultCallback): void {
    this.onTokenRefreshResultCallback = callback;
  }

  public setAuthUserId(userId: string | null): void {
    if (this.authUserId !== userId) {
      this.execution.clearCompleted();
    }

    this.authUserId = userId;
  }

  public getAuthUserId(): string | null {
    return this.authUserId;
  }

  public destroy(): void {
    if (this.isDestroyed) {
      return;
    }

    this.isDestroyed = true;
    this.execution.destroy();
    this.delegation.destroy();
    this.requests.destroy();
  }

  private handleTokenRefreshSuccess(data: TokenRefreshSuccess): void {
    if (!data.requestId) {
      this.delegation.complete(data.delegationRequestId, getTokenResponseUserId(data));
    }
    this.requests.handleSuccess(data, this.authUserId);
  }

  private handleTokenRefreshFailed(data: TokenRefreshFailure): void {
    if (data.delegateRefresh) {
      const pendingRequest = this.delegation.accept(
        data,
        this.requests.get(data.requestId),
        this.transport.tabId
      );
      if (pendingRequest) {
        this.refreshAsDelegatedOwner(data.requestId, pendingRequest);
      }
      return;
    }

    this.requests.handleFailure(data);
  }

  private async refreshAsDelegatedOwner(requestId: string, pendingRequest: PendingRefreshRequest) {
    const { requestedByUserId, requestStartedAt } = pendingRequest;

    if (!this.isCurrentAuthUser(requestedByUserId)) {
      return;
    }

    try {
      const tokenData = await this.execution.execute(
        requestedByUserId,
        requestStartedAt,
        pendingRequest.recovery
      );

      if (this.isDestroyed || !this.requests.isPending(requestId, pendingRequest)) {
        return;
      }

      if (!tokenData || getTokenResponseUserId(tokenData) !== requestedByUserId) {
        this.requests.complete(requestId, pendingRequest, createNoUsableTokenRefreshResult());
        return;
      }

      const completedDelegation = { ...tokenData, delegationRequestId: requestId };
      this.broadcastTokenUpdate(completedDelegation);
      this.requests.complete(requestId, pendingRequest, { success: true });
      this.handleTokenRefreshSuccess(completedDelegation);
    } catch (error) {
      this.requests.complete(requestId, pendingRequest, createTokenRefreshFailureResult(error));
    }
  }

  private isCurrentAuthUser(userId?: string | null): userId is string {
    return Boolean(userId && userId === this.authUserId);
  }

  private getRefreshRequestUserId(requestedByUserId?: string | null) {
    if (requestedByUserId === undefined) {
      return this.authUserId;
    }

    return this.isCurrentAuthUser(requestedByUserId) ? requestedByUserId : null;
  }

  private async handleTokenRefreshRequest(data: TokenRefreshRequest) {
    if (this.isDestroyed || !this.transport.isLeader()) {
      return;
    }

    if (this.delegation.isActive()) {
      return;
    }

    const requestStartedAt = getTokenRefreshRequestStartedAt(data.requestId, data.requestStartedAt);

    if (
      requestStartedAt !== undefined &&
      Date.now() - requestStartedAt >= TOKEN_RECOVERY_EPISODE_TIMEOUT_MS
    ) {
      return;
    }

    const requestedByUserId = this.getRefreshRequestUserId(data.requestedByUserId);

    if (!requestedByUserId) {
      if (data.requestedByUserId) {
        if (this.execution.isActive()) {
          return;
        }

        const grant = this.delegation.grant(data, data.requestedByUserId, requestStartedAt);
        if (grant) {
          this.transport.postMessage({ type: 'TOKEN_REFRESH_FAILED', data: grant });
          return;
        }
      }

      this.transport.postMessage({
        type: 'TOKEN_REFRESH_FAILED',
        data: {
          requestId: data.requestId,
          error: createDifferentAuthUserRefreshResult().error,
          terminalFailure: false,
          refreshLocally: true
        }
      });
      return;
    }

    try {
      const refreshPromise = this.execution.execute(
        requestedByUserId,
        requestStartedAt,
        sanitizeTokenRecoveryDiagnostic(data.recovery)
      );
      const activeRecovery = this.execution.getActiveRecovery();
      if (activeRecovery) {
        this.transport.postMessage({
          type: 'TOKEN_REFRESH_STARTED',
          data: { requestId: data.requestId, recovery: activeRecovery }
        });
      }
      const tokenData = await refreshPromise;

      if (this.isDestroyed) {
        return;
      }

      if (!tokenData) {
        const { error, terminalFailure } = createNoUsableTokenRefreshResult();

        this.transport.postMessage({
          type: 'TOKEN_REFRESH_FAILED',
          data: {
            requestId: data.requestId,
            error,
            terminalFailure
          }
        });
        return;
      }

      this.transport.postMessage({
        type: 'TOKEN_REFRESH_SUCCESS',
        data: {
          requestId: data.requestId,
          ...tokenData
        }
      });
    } catch (error) {
      if (this.isDestroyed) {
        return;
      }

      const failureResult = createTokenRefreshFailureResult(error);
      const failureData: TokenRefreshFailure = {
        requestId: data.requestId,
        error: failureResult.error,
        status: failureResult.status,
        terminalFailure: failureResult.terminalFailure,
        ...(failureResult.diagnosticSummary && {
          diagnosticSummary: failureResult.diagnosticSummary
        })
      };

      if (failureResult.refreshLocally) {
        failureData.refreshLocally = true;
      }

      this.transport.postMessage({
        type: 'TOKEN_REFRESH_FAILED',
        data: failureData
      });
    }
  }

  private async refreshAsLeader(
    requestedByUserId?: string | null,
    requestStartedAt?: number,
    recovery?: TokenRecoveryDiagnostic
  ): Promise<TokenRefreshAttemptResult> {
    if (this.delegation.isActive()) {
      return createCoordinationFailure(
        createRefreshOwnedByAnotherTabResult(),
        recovery,
        'owner_busy'
      );
    }

    if (!this.isCurrentAuthUser(requestedByUserId)) {
      return createCoordinationFailure(
        createDifferentAuthUserRefreshResult(),
        recovery,
        'user_mismatch'
      );
    }

    try {
      const tokenData = await this.execution.execute(requestedByUserId, requestStartedAt, recovery);

      if (this.isDestroyed) {
        return createDestroyedRefreshResult();
      }

      if (tokenData) {
        this.broadcastTokenUpdate(tokenData);
        return { success: true };
      }

      return createCoordinationFailure(createNoUsableTokenRefreshResult(), recovery, 'no_token');
    } catch (error) {
      return createTokenRefreshFailureResult(error);
    }
  }

  private async performTokenRefresh(
    requestedByUserId?: string | null,
    recovery?: TokenRecoveryDiagnostic
  ): Promise<TokenRefreshAttemptResult> {
    const requestStartedAt = Date.now();

    if (this.isDestroyed) {
      return createDestroyedRefreshResult();
    }

    const scopedRequestedByUserId = requestedByUserId ?? this.authUserId;

    if (!scopedRequestedByUserId) {
      return createDifferentAuthUserRefreshResult();
    }

    if (this.transport.isLeader()) {
      return this.refreshAsLeader(scopedRequestedByUserId, requestStartedAt, recovery);
    }

    const hasLeader = await this.transport.ensureLeadership();

    if (this.isDestroyed) {
      return createDestroyedRefreshResult();
    }

    if (!hasLeader) {
      return createCoordinationFailure(
        {
          success: false,
          error: 'No leader became available for coordinated token refresh',
          terminalFailure: false
        },
        recovery,
        'no_leader'
      );
    }

    if (this.transport.isLeader()) {
      return this.refreshAsLeader(scopedRequestedByUserId, requestStartedAt, recovery);
    }

    const { requestId, response } = this.requests.create(
      scopedRequestedByUserId,
      requestStartedAt,
      recovery
    );

    if (this.isDestroyed) {
      return response;
    }

    this.transport.postMessage({
      type: 'TOKEN_REFRESH_REQUEST',
      data: {
        requestId,
        requestStartedAt,
        requestedByTabId: this.transport.tabId,
        requestedByUserId: scopedRequestedByUserId,
        recovery
      }
    });

    return response;
  }
}
